import { oc } from "@orpc/contract";
import { z } from "zod";

export const DataSourceSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  title: z.string(),
  isSource: z.boolean(),
  lastRead: z.string().datetime().nullable(),
  isSink: z.boolean(),
  lastWrite: z.string().datetime().nullable(),
});

export const DataSourceGroupSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  dataSources: z.array(DataSourceSchema),
});

export const ListDataSourcesResponseSchema = z.object({
  groups: z.array(DataSourceGroupSchema),
});

export const CreateDataSourceInputSchema = z.object({
  title: z.string().min(1),
  groupId: z.string().uuid().optional(),
  newGroupTitle: z.string().min(1).optional(),
  isSource: z.boolean(),
  isSink: z.boolean(),
});

export const CreateDataSourceResponseSchema = z.object({
  dataSource: DataSourceSchema,
  createdGroup: DataSourceGroupSchema.omit({ dataSources: true }).nullable(),
});

export const UpdateDataSourceGroupInputSchema = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
});

export const UpdateDataSourceInputSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1),
  groupId: z.string().uuid().optional(),
  newGroupTitle: z.string().min(1).optional(),
  isSource: z.boolean(),
  isSink: z.boolean(),
});

export const UpdateDataSourceResponseSchema = z.object({
  dataSource: DataSourceSchema,
  createdGroup: DataSourceGroupSchema.omit({ dataSources: true }).nullable(),
});

export const DeleteDataSourceInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateDataSourceGroupInputSchema = z.object({
  title: z.string().min(1),
});

export const DeleteDataSourceGroupInputSchema = z.object({
  id: z.string().uuid(),
});

export const CreateDataSourceGroupResponseSchema = z.object({
  group: DataSourceGroupSchema.omit({ dataSources: true }),
});

export const DataSourceLinkSchema = z.object({
  id: z.string().uuid(),
  dataSourceId: z.string().uuid(),
  schemaId: z.string().uuid(),
  schemaName: z.string(),
  foreignSchemaName: z.string(),
  foreignTableName: z.string(),
});

export const ListDataSourceLinksResponseSchema = z.object({
  links: z.array(DataSourceLinkSchema),
});

export const DataSourceLinkGroupSchema = z.object({
  sourceTitle: z.string(),
  links: z.array(DataSourceLinkSchema),
});

export const ListAllDataSourceLinksResponseSchema = z.object({
  groups: z.array(DataSourceLinkGroupSchema),
});

export const CreateDataSourceLinkInputSchema = z.object({
  dataSourceId: z.string().uuid(),
  schemaId: z.string().uuid(),
  foreignSchemaName: z.string().min(1),
  foreignTableName: z.string().min(1),
});

export const UpdateDataSourceLinkInputSchema = z.object({
  id: z.string().uuid(),
  schemaId: z.string().uuid(),
  foreignSchemaName: z.string().min(1),
  foreignTableName: z.string().min(1),
});

export const DeleteDataSourceLinkInputSchema = z.object({
  id: z.string().uuid(),
});

export const SchemaMismatchSchema = z.object({
  columnName: z.string(),
  issue: z.enum([
    "missing_column",
    "type_mismatch",
    "nullability_mismatch",
    "default_mismatch",
  ]),
  // For type/nullability/default issues these carry human-readable values
  // (e.g. "NOT NULL" vs "nullable", or the expected vs actual default).
  expectedType: z.string().optional(),
  actualType: z.string().nullable().optional(),
});

export const ValidateDataSourceLinkResponseSchema = z.object({
  status: z.enum(["ok", "missing", "mismatch"]),
  mismatches: z.array(SchemaMismatchSchema),
});

export const dataSourcesContracts = {
  list: oc
    .input(z.object({}))
    .output(ListDataSourcesResponseSchema)
    .route({ method: "GET" }),
  listLinks: oc
    .input(z.object({ dataSourceId: z.string().uuid() }))
    .output(ListDataSourceLinksResponseSchema)
    .route({ method: "GET" }),
  listAllLinks: oc
    .input(z.object({}))
    .output(ListAllDataSourceLinksResponseSchema)
    .route({ method: "GET" }),
  createLink: oc
    .input(CreateDataSourceLinkInputSchema)
    .output(z.object({ link: DataSourceLinkSchema }))
    .route({ method: "POST" }),
  updateLink: oc
    .input(UpdateDataSourceLinkInputSchema)
    .output(z.object({ link: DataSourceLinkSchema }))
    .route({ method: "POST" }),
  deleteLink: oc
    .input(DeleteDataSourceLinkInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  createGroup: oc
    .input(CreateDataSourceGroupInputSchema)
    .output(CreateDataSourceGroupResponseSchema)
    .route({ method: "POST" }),
  deleteGroup: oc
    .input(DeleteDataSourceGroupInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  create: oc
    .input(CreateDataSourceInputSchema)
    .output(CreateDataSourceResponseSchema)
    .route({ method: "POST" }),
  update: oc
    .input(UpdateDataSourceInputSchema)
    .output(UpdateDataSourceResponseSchema)
    .route({ method: "POST" }),
  delete: oc
    .input(DeleteDataSourceInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  updateGroup: oc
    .input(UpdateDataSourceGroupInputSchema)
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
  validateLink: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(ValidateDataSourceLinkResponseSchema)
    .route({ method: "GET" }),
  createLinkTable: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(z.object({ success: z.literal(true) }))
    .route({ method: "POST" }),
} as const;

export type DataSource = z.infer<typeof DataSourceSchema>;
export type DataSourceGroup = z.infer<typeof DataSourceGroupSchema>;
export type ListDataSourcesResponse = z.infer<
  typeof ListDataSourcesResponseSchema
>;
export type CreateDataSourceInput = z.infer<typeof CreateDataSourceInputSchema>;
export type UpdateDataSourceInput = z.infer<typeof UpdateDataSourceInputSchema>;
export type CreateDataSourceGroupInput = z.infer<
  typeof CreateDataSourceGroupInputSchema
>;
export type DataSourceLink = z.infer<typeof DataSourceLinkSchema>;
export type DataSourceLinkGroup = z.infer<typeof DataSourceLinkGroupSchema>;
export type ListAllDataSourceLinksResponse = z.infer<
  typeof ListAllDataSourceLinksResponseSchema
>;
export type CreateDataSourceLinkInput = z.infer<
  typeof CreateDataSourceLinkInputSchema
>;
export type UpdateDataSourceLinkInput = z.infer<
  typeof UpdateDataSourceLinkInputSchema
>;
export type SchemaMismatch = z.infer<typeof SchemaMismatchSchema>;
export type ValidateDataSourceLinkResponse = z.infer<
  typeof ValidateDataSourceLinkResponseSchema
>;
